import { useEffect, useState } from 'react'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import { DateTime } from 'luxon'
import styled, { css, keyframes } from 'styled-components'
import { TASK_PRIORITIES } from '../../../constants'
import { useNavigateToTask } from '../../../hooks'
import useIsReadOnly from '../../../hooks/useIsReadOnly'
import { Border, Colors, Spacing, Typography } from '../../../styles'
import { pointerQuery } from '../../../styles/dimensions'
import { DropType, TSubtask } from '../../../utils/types'
import Domino from '../../atoms/Domino'
import DueDate from '../../atoms/DueDate'
import { Icon } from '../../atoms/Icon'
import MarkTaskDoneButton from '../../atoms/buttons/MarkTaskDoneButton'
import TaskContextMenuWrapper from '../../radix/TaskContextMenuWrapper'
import { ReorderActions } from '../../radix/reorderMenuItems'

const strike = keyframes`
    0% { width: 0; }
    100% { width: 100%; }
`

export const SubtaskDropOffset = styled.div`
    width: 100%;
    padding: 2px 0;
`
const PositionedDomino = styled(Domino)`
    position: absolute;
    inset-inline-start: 0;
    top: 50%;
    transform: translate(-50%, -50%);
`
export const RightContainer = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: ${Spacing._12};
    white-space: nowrap;
`
export const SubtaskContainer = styled.div<{ forceHoverStyle?: boolean; isDone?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    gap: ${Spacing._8};
    border: ${Border.stroke.small} solid ${Colors.background.border};
    border-radius: ${Border.radius.medium};
    padding: ${Spacing._16};
    ${Typography.body.medium};
    cursor: pointer;
    /* See ItemContainer: a stuck :hover after a tap reads as a selected row. */
    ${pointerQuery.canHover} {
        :hover {
            background-color: ${Colors.background.sub};
        }
    }
    ${pointerQuery.noHover} {
        :active {
            background-color: ${Colors.background.sub};
        }
    }
    ${({ forceHoverStyle }) => forceHoverStyle && `background-color: ${Colors.background.sub};`}
    user-select: none;
    width: 100%;
    box-sizing: border-box;
    ${({ isDone }) => isDone && `background-color: ${Colors.background.base};`}
`
export const TitleSpan = styled.span<{ isDone: boolean; shouldAnimate: boolean }>`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    position: relative;
    color: ${({ isDone }) => (isDone ? Colors.text.light : Colors.text.ink)};

    ${({ isDone, shouldAnimate }) =>
        isDone &&
        css`
            &::after {
                content: ' ';
                position: absolute;
                left: 0;
                top: 50%;
                width: 100%;
                height: 1px;
                background-color: ${Colors.text.light};
                animation-name: ${strike};
                animation-duration: ${shouldAnimate ? '0.2s' : '0s'};
                animation-timing-function: ease-in;
                animation-iteration-count: 1;
                animation-fill-mode: forwards;
            }
        `}
`

interface SubtaskProps {
    subtask: TSubtask
    reorder?: ReorderActions
}
const Subtask = ({ subtask, reorder }: SubtaskProps) => {
    const navigateToTask = useNavigateToTask()
    const [isVisible, setIsVisible] = useState(false)
    const dueDate = DateTime.fromISO(subtask.due_date)

    const visibilityToggle = {
        onMouseEnter: () => setIsVisible(true),
        onMouseLeave: () => setIsVisible(false),
    }

    const isReadOnly = useIsReadOnly()
    const [, drag, dragPreview] = useDrag(
        () => ({
            type: DropType.SUBTASK,
            item: { id: subtask.id, task: subtask },
            canDrag: !isReadOnly,
        }),
        [subtask, isReadOnly]
    )
    useEffect(() => {
        dragPreview(getEmptyImage())
    }, [])

    const [contextMenuOpen, setContextMenuOpen] = useState(false)
    const [shouldAnimate, setShouldAnimate] = useState(false)

    return (
        <SubtaskDropOffset>
            <TaskContextMenuWrapper task={subtask} onOpenChange={setContextMenuOpen} reorder={reorder}>
                <SubtaskContainer
                    onClick={() => navigateToTask({ taskId: subtask.id })}
                    ref={drag}
                    {...visibilityToggle}
                    forceHoverStyle={contextMenuOpen}
                    isDone={subtask.is_done}
                    data-testid="subtask-row"
                >
                    {/* The row itself remains draggable. Keep its visual handle out of flow so the
                        16px row padding is the one hierarchy indent on both pointer modes. */}
                    <PositionedDomino isVisible={isVisible} />
                    <MarkTaskDoneButton
                        isDone={subtask.is_done}
                        taskId={subtask.id}
                        isSelected={false}
                        onMarkComplete={() => setShouldAnimate(true)}
                    />
                    <TitleSpan data-subtask-title isDone={subtask.is_done} shouldAnimate={shouldAnimate}>
                        {subtask.title}
                    </TitleSpan>
                    <RightContainer data-subtask-metadata>
                        <DueDate date={dueDate} isDoneOrDeleted={subtask.is_done || subtask.is_deleted} />
                        {subtask.priority_normalized !== 0 && (
                            <Icon
                                icon={TASK_PRIORITIES[subtask.priority_normalized].icon}
                                color={TASK_PRIORITIES[subtask.priority_normalized].color}
                            />
                        )}
                    </RightContainer>
                </SubtaskContainer>
            </TaskContextMenuWrapper>
        </SubtaskDropOffset>
    )
}

export default Subtask
